"use client";
import { motion } from "framer-motion";
import React from "react";

/**
 * KJRST brand loader
 * - White background friendly
 * - Subtle black track + green progress (#16a34a)
 * - Animated ring with "KJRS" centered
 */
export default function KJRSLoader({
  size = 80, // overall size (px)
  strokeWidth = 8, // ring thickness
  showLabel = true, // show "Loading..." label
}) {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;

  return (
    <div className="flex flex-col items-center justify-center p-6 select-none">
      <div
        className="relative"
        style={{ width: size, height: size }}
        aria-busy="true"
        aria-live="polite"
        role="status"
      >
        {/* Static track (subtle black) */}
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="block"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(0,0,0,0.12)" // subtle black track
            strokeWidth={strokeWidth}
          />
        </svg>

        {/* Animated progress ring */}
        <motion.svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="absolute inset-0"
          style={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "linear" }}
        >
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="#16a34a" // Kuya J green
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={c}
            // animate a sweeping dash for a lively feel
            initial={{ strokeDashoffset: c * 0.75 }}
            animate={{ strokeDashoffset: [c * 0.75, c * 0.25, c * 0.75] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          />
        </motion.svg>

        {/* Center text: KJRS */}
        <div className="absolute inset-0 grid place-items-center">
          <motion.span
            className="font-extrabold tracking-widest select-none"
            style={{ fontSize: Math.max(14, size * 0.22) }}
            animate={{ opacity: [0.6, 1, 0.6], scale: [1, 1.02, 1] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
          >
            <span className="text-black">KJRS</span>
          </motion.span>
        </div>

        {/* Small inner accent dot (black) */}
        <div
          className="absolute rounded-full"
          style={{
            width: strokeWidth / 2,
            height: strokeWidth / 2,
            background: "rgba(0,0,0,0.9)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      </div>

      {showLabel && (
        <motion.div
          className="mt-3 text-sm font-medium"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 1.4 }}
        >
          <span className="text-black/80">Loading</span>
          <span className="text-[#16a34a]"> …</span>
        </motion.div>
      )}
    </div>
  );
}
